import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Mic, Square, Play, Pause, Download } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AudioWaveform from "@/components/AudioWaveform";
import TranscriptPanel from "@/components/TranscriptPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [sessionTitle, setSessionTitle] = useState("");
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  
  // New states for call selection
  const [incompleteCalls, setIncompleteCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [isLoadingCalls, setIsLoadingCalls] = useState(true);
  const [contextPrompt, setContextPrompt] = useState(""); // Context for better transcription accuracy
  
  const mediaRecorderRef = useRef(null);
  const streamIdRef = useRef(null);
  const seqRef = useRef(0);
  const uploadQueueRef = useRef(Promise.resolve());
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const sessionResponseRef = useRef(null); // Store session for use in handlers

  // Prevent tab close while recording and set global flag
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRecording) {
        e.preventDefault();
        e.returnValue = 'Recording is in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    // Set global flag for navigation check (used by Navigation component)
    window.isRecordingInProgress = isRecording;

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.isRecordingInProgress = false;
    };
  }, [isRecording]);

  useEffect(() => {
    // Fetch incomplete calls on mount
    fetchIncompleteCalls();
  }, []);

  const fetchIncompleteCalls = async () => {
    try {
      setIsLoadingCalls(true);
      const response = await axios.get(`${API}/callmaster/incomplete/list`);
      setIncompleteCalls(response.data);
    } catch (error) {
      console.error("Error fetching incomplete calls:", error);
      toast.error("Failed to load calls");
    } finally {
      setIsLoadingCalls(false);
    }
  };

  // Upload pieces strictly in order, retrying until each succeeds
  const enqueueChunk = (blob) => {
    const seq = seqRef.current++;
    const streamId = streamIdRef.current;
    uploadQueueRef.current = uploadQueueRef.current.then(async () => {
      for (let attempt = 0; attempt < 10; attempt++) {
        try {
          const formData = new FormData();
          formData.append('seq', seq.toString());
          formData.append('chunk', blob, `chunk_${seq}.webm`);
          await axios.post(`${API}/stream/${streamId}/chunk`, formData);
          return;
        } catch (error) {
          console.error(`Chunk ${seq} upload failed (attempt ${attempt + 1})`, error);
          await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        }
      }
      toast.error(`Audio piece ${seq} could not be uploaded`);
    });
  };

  // Poll transcriptions produced by the backend worker
  useEffect(() => {
    if (!selectedCall || !startTime) return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/transcriptions`, {
          params: { ticker: selectedCall.ticker, calldatetime: selectedCall.calldatetime }
        });
        const startedAt = startTime.getTime();
        setTranscript(res.data
          .filter(t => new Date(t.created_at).getTime() >= startedAt - 5000)
          .map(t => ({ id: t.id, text: t.transcription_text, timestamp: t.timestamp })));
      } catch (error) {
        console.error("Error loading transcriptions:", error);
      }
    };
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [selectedCall, startTime]);

  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording, isPaused]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      const audioTrack = stream.getAudioTracks()[0];
      if (!audioTrack) {
        toast.error("No audio track found. Please share tab with audio.");
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      streamRef.current = stream;

      const audioStream = new MediaStream([audioTrack]);
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(audioStream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      updateAudioLevel();

      const sessionResponse = await axios.post(`${API}/sessions`, {
        title: `Recording ${new Date().toLocaleString()}`
      });
      setCurrentSession(sessionResponse.data);
      setSessionTitle(sessionResponse.data.title);
      setStartTime(new Date());
      setEndTime(null);
      sessionResponseRef.current = sessionResponse;

      const streamRes = await axios.post(`${API}/stream/start`, {
        call_id: selectedCall.id,
        prompt: contextPrompt.trim()
      });
      streamIdRef.current = streamRes.data.stream_id;
      seqRef.current = 0;
      uploadQueueRef.current = Promise.resolve();

      // Single continuous recorder: never stops, emits 1s pieces that the server appends to one file
      const mediaRecorder = new MediaRecorder(audioStream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) enqueueChunk(event.data);
      };
      mediaRecorder.onerror = (e) => {
        console.error('Recorder error:', e.error);
        toast.error('Recorder error');
      };
      mediaRecorder.start(1000);

      setIsRecording(true);
      setTranscript([]);
      setRecordingTime(0);
      toast.success("Recording started");

    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Failed to start recording. Please ensure you selected a tab with audio.");
    }
  };

  const updateAudioLevel = () => {
    if (analyserRef.current && isRecording && !isPaused) {
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setAudioLevel(average / 255);
      animationRef.current = requestAnimationFrame(updateAudioLevel);
    }
  };

  const stopRecording = async () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      await new Promise((resolve) => {
        recorder.addEventListener('stop', resolve, { once: true });
        recorder.stop();
      });
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    if (currentSession) {
      await axios.patch(`${API}/sessions/${currentSession.id}/duration`, null, {
        params: { duration: recordingTime }
      });
    }

    setIsRecording(false);
    setIsPaused(false);
    setAudioLevel(0);
    setEndTime(new Date());
    
    try {
      await uploadQueueRef.current;
      await axios.post(`${API}/stream/${streamIdRef.current}/stop`);
      toast.success("Recording stopped - finishing last segment");
    } catch (error) {
      console.error("Error stopping stream:", error);
      toast.error("Failed to finalize recording");
    }
  };

  const togglePause = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    if (isPaused) {
      recorder.resume();
      updateAudioLevel();
      toast.info("Recording resumed");
    } else {
      recorder.pause();
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      toast.info("Recording paused");
    }
    setIsPaused(!isPaused);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const exportTranscript = () => {
    const text = transcript.map(item => `[${item.timestamp}] ${item.text}`).join('\n\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Transcript exported");
  };

  return (
    <div className="min-h-screen bg-deepspace-bg font-dmsans" style={{ minHeight: '100vh', paddingTop: '0' }}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 h-screen overflow-hidden" style={{ height: 'calc(100vh - 0px)' }}>
        <div className="col-span-1 lg:col-span-5 flex flex-col gap-4 h-full overflow-y-auto">

          {/* Call Selection Dropdown */}
          <Card className="bg-deepspace-surface border-deepspace-border shadow-xl rounded-xl p-4" data-testid="call-selection-card">
            <h2 className="text-lg font-semibold font-outfit text-[#EDEDED] mb-3">
              Select Call to Record
            </h2>
            {isLoadingCalls ? (
              <p className="text-[#A1A1AA] text-sm">Loading calls...</p>
            ) : incompleteCalls.length === 0 ? (
              <p className="text-[#A1A1AA] text-sm">
                No pending calls. Please add a call in Admin Console first.
              </p>
            ) : (
              <select
                value={selectedCall?.id || ""}
                onChange={(e) => {
                  const call = incompleteCalls.find(c => c.id === e.target.value);
                  setSelectedCall(call);
                  if (call) {
                    toast.success(`Selected: ${call.ticker} - ${new Date(call.calldatetime).toLocaleString()}`);
                  }
                }}
                disabled={isRecording || isLoadingCalls}
                className="w-full bg-deepspace-bg border border-deepspace-border text-[#EDEDED] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-volt-blue disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="call-dropdown"
              >
                <option value="">-- Select a call --</option>
                {incompleteCalls.map((call) => (
                  <option key={call.id} value={call.id}>
                    {new Date(call.calldatetime).toLocaleString()} - {call.ticker}
                  </option>
                ))}
              </select>
            )}
            {!selectedCall && !isRecording && (
              <p className="text-xs text-[#EF4444] mt-2">
                ⚠️ Please select a call before recording
              </p>
            )}
          </Card>

          {/* Context Prompt for Better Accuracy */}
          <Card className="bg-deepspace-surface border-deepspace-border shadow-xl rounded-xl p-4" data-testid="context-prompt-card">
            <h2 className="text-lg font-semibold font-outfit text-[#EDEDED] mb-2">
              Context for Accuracy
            </h2>
            <p className="text-xs text-[#A1A1AA] mb-3">
              Add company names, ticker symbols, industry terms, and key people's names to improve transcription accuracy.
            </p>
            <textarea
              value={contextPrompt}
              onChange={(e) => setContextPrompt(e.target.value)}
              disabled={isRecording}
              placeholder="Example: TEVA Pharmaceutical, earnings call, Q3 2026, CEO Kåre Schultz, generic drugs, biosimilars, Ajovy, Austedo, revenue guidance"
              className="w-full bg-deepspace-bg border border-deepspace-border text-[#EDEDED] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-volt-blue disabled:opacity-50 disabled:cursor-not-allowed resize-none"
              rows={3}
              data-testid="context-prompt-input"
            />
            {contextPrompt && (
              <p className="text-xs text-green-500 mt-2">
                ✓ Context will be used for improved accuracy
              </p>
            )}
          </Card>

          <Card className="bg-deepspace-surface border-deepspace-border shadow-xl rounded-xl p-4" data-testid="recording-controls-card">
            <AudioWaveform isRecording={isRecording} isPaused={isPaused} audioLevel={audioLevel} />
            
            <div className="flex items-center justify-center gap-4 mt-4">
              {!isRecording ? (
                <Button
                  onClick={startRecording}
                  disabled={!selectedCall}
                  className="px-6 py-3 rounded-full bg-recording-red hover:bg-recording-red/80 text-white shadow-lg hover:shadow-recording-red/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="start-recording-btn"
                >
                  <Mic className="w-5 h-5 mr-2" />
                  Record
                </Button>
              ) : (
                <>
                  <Button
                    onClick={togglePause}
                    className="px-5 py-2 rounded-full bg-volt-blue hover:bg-volt-blue/80 text-white transition-all duration-300"
                    data-testid="pause-recording-btn"
                  >
                    {isPaused ? <Play className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
                    {isPaused ? "Resume" : "Pause"}
                  </Button>
                  <Button
                    onClick={stopRecording}
                    className="px-6 py-3 rounded-full bg-recording-red hover:bg-recording-red/80 text-white recording-pulse transition-all duration-300"
                    data-testid="stop-recording-btn"
                  >
                    <Square className="w-5 h-5 mr-2" />
                    Stop
                  </Button>
                </>
              )}
            </div>

            <div className="text-center mt-3">
              <div className="text-2xl font-jetbrains text-[#EDEDED] tracking-wide" data-testid="recording-timer">
                {formatTime(recordingTime)}
              </div>
              <div className="text-xs text-[#A1A1AA] mt-1">
                {isRecording ? (isPaused ? "Paused" : isTranscribing ? "Transcribing..." : "Recording...") : "Ready to record"}
              </div>
            </div>
          </Card>

          {transcript.length > 0 && (
            <Card className="bg-deepspace-surface border-deepspace-border shadow-xl rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold font-outfit text-[#EDEDED]">Quick Actions</h2>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={exportTranscript}
                  className="flex-1 bg-volt-blue hover:bg-volt-blue/80 text-white transition-all duration-300 text-sm py-2"
                  data-testid="export-transcript-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Full Transcript
                </Button>
              </div>
            </Card>
          )}
        </div>

        <div className="col-span-1 lg:col-span-7 h-full">
          <TranscriptPanel 
            transcript={transcript} 
            isRecording={isRecording} 
            isTranscribing={isTranscribing}
            sessionTitle={sessionTitle}
            startTime={startTime}
            endTime={endTime}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;